// PageHeader — the eyebrow + title + description + right-aligned actions block
// that opens every route. Standardises the spacing and type scale that each
// page header used to set by hand (frontend audit 2026-10-02: 7 distinct <h1>
// class strings across 33 pages, 30 pages not using this component at all).
//
// `icon` and `meta` cover the two shapes pages were hand-rolling around the
// title: a leading lucide icon, and a trailing count/status ("128 shown",
// a Badge). Both sit on the title baseline so the eyebrow/title/description
// rhythm stays identical whether or not they are present.
import { ReactNode } from 'react';
import { cn } from './cn';

export interface PageHeaderProps {
  readonly eyebrow?: string;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  /** Leading icon, rendered at title size in the accent colour (the lint mint). */
  readonly icon?: ReactNode;
  /** Trailing inline meta next to the title: a count, a Badge, a status. */
  readonly meta?: ReactNode;
  /** Visual size. `page` for route titles (default), `section` for nested panels. */
  readonly size?: 'page' | 'section';
  readonly className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  icon,
  meta,
  size = 'page',
  className,
}: PageHeaderProps) {
  const Heading = size === 'page' ? 'h1' : 'h2';
  return (
    <header
      className={cn(
        'flex items-start justify-between gap-4',
        size === 'page' ? 'mb-6' : 'mb-4',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow ? <p className="orb-eyebrow mb-1">{eyebrow}</p> : null}
        <div className="flex items-baseline gap-3 flex-wrap">
          <Heading
            className={cn(
              'font-display font-semibold text-fg flex items-center gap-2',
              size === 'page' ? 'text-3xl' : 'text-xl',
            )}
          >
            {icon ? (
              <span className="text-accent shrink-0 [&>svg]:h-[0.8em] [&>svg]:w-[0.8em]" aria-hidden="true">
                {icon}
              </span>
            ) : null}
            {title}
          </Heading>
          {meta ? <span className="text-xs text-fg-muted">{meta}</span> : null}
        </div>
        {description ? (
          <p className="text-fg-muted text-sm mt-1 max-w-2xl">{description}</p>
        ) : null}
      </div>
      {actions ? (
        // Actions sit on the title row, not the bottom of the description —
        // the eyebrow line is skipped with a top margin so the buttons align
        // with the <h1> regardless of how long the description wraps.
        <div className={cn('flex items-center gap-2 shrink-0', eyebrow && 'mt-6')}>{actions}</div>
      ) : null}
    </header>
  );
}
